import { Fragment, useState } from 'react';
import {
  ActionIcon,
  Badge,
  Button,
  Group,
  Menu,
  Pagination,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import dayjs from 'dayjs';
import { IconChevronDown, IconChevronRight, IconPlus, IconX } from '@tabler/icons-react';
import CompanyWrapper from 'src/company/templates/components/CompanyWrapper';
import { Company } from 'src/secret-menu/hooks/useCompanies';
import PageLoader from 'src/UI/loader/PageLoader';
import {
  ExplorerEvent,
  ExplorerFilters,
  ExplorerParticipant,
  PAGE_SIZE,
  useMeetingsExplorer,
} from './useMeetingsExplorer';

export default function MeetingsExplorerTab() {
  return <CompanyWrapper contentComponent={MeetingsExplorerContent} />;
}

type FilterField = {
  key: keyof ExplorerFilters;
  label: string;
  group: 'Event' | 'Client' | 'Calendar';
  placeholder: string;
  type?: 'date';
};

const FILTER_FIELDS: FilterField[] = [
  { key: 'title', label: 'Title', group: 'Event', placeholder: 'Contains every word…' },
  { key: 'scheduledFrom', label: 'Scheduled from', group: 'Event', placeholder: '', type: 'date' },
  { key: 'scheduledTo', label: 'Scheduled before', group: 'Event', placeholder: '', type: 'date' },
  { key: 'clientFirstName', label: 'Client first name', group: 'Client', placeholder: 'Contains…' },
  { key: 'clientLastName', label: 'Client last name', group: 'Client', placeholder: 'Contains…' },
  { key: 'clientEmail', label: 'Client email', group: 'Client', placeholder: 'Contains…' },
  { key: 'clientId', label: 'Client ID', group: 'Client', placeholder: 'UUID' },
  {
    key: 'attendeeEmail',
    label: 'Attendee email',
    group: 'Client',
    placeholder: 'Any attendee, linked to a client or not…',
  },
  { key: 'calendarEmail', label: 'Calendar email', group: 'Calendar', placeholder: 'Contains…' },
  { key: 'calendarId', label: 'Calendar ID', group: 'Calendar', placeholder: 'UUID' },
  { key: 'ownerUserId', label: 'Calendar owner user ID', group: 'Calendar', placeholder: 'UUID' },
];

const FILTER_GROUPS = ['Event', 'Client', 'Calendar'] as const;

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const UUID_FIELDS = new Set<keyof ExplorerFilters>(['clientId', 'calendarId', 'ownerUserId']);

export function MeetingsExplorerContent({ company }: { company: Company }) {
  const [filters, setFilters] = useState<ExplorerFilters>({});
  const [activeFilterKeys, setActiveFilterKeys] = useState<(keyof ExplorerFilters)[]>(['title']);
  const [page, setPage] = useState(1);
  const [debouncedFilters] = useDebouncedValue(filters, 400);
  const { data, isLoading, isError, isFetching } = useMeetingsExplorer(
    company.id,
    toQueryFilters(debouncedFilters),
    page,
  );

  const setFilter = (key: keyof ExplorerFilters, value: string) => {
    setFilters(prev => ({ ...prev, [key]: value || undefined }));
    setPage(1);
  };

  const removeFilter = (key: keyof ExplorerFilters) => {
    setActiveFilterKeys(keys => keys.filter(activeKey => activeKey !== key));
    setFilter(key, '');
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / PAGE_SIZE)) : 1;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <Title order={3}>Meetings explorer</Title>
        {data && (
          <Text size="sm" c="dimmed">
            {data.total} events{isFetching ? ' (refreshing…)' : ''}
          </Text>
        )}
      </Group>
      <Text size="sm" c="dimmed">
        Raw calendar events across every calendar in this tenant, newest first. Nothing is hidden:
        deleted events, events on deleted calendars and removed attendees are all shown.
      </Text>

      <FilterBuilder
        filters={filters}
        activeKeys={activeFilterKeys}
        onAdd={key => setActiveFilterKeys(keys => [...keys, key])}
        onRemove={removeFilter}
        onChange={setFilter}
      />

      <ExplorerResults
        events={data?.items}
        isLoading={isLoading}
        isError={isError}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        highlightClientId={debouncedFilters.clientId}
      />
    </Stack>
  );
}

function FilterBuilder({
  filters,
  activeKeys,
  onAdd,
  onRemove,
  onChange,
}: {
  filters: ExplorerFilters;
  activeKeys: (keyof ExplorerFilters)[];
  onAdd: (key: keyof ExplorerFilters) => void;
  onRemove: (key: keyof ExplorerFilters) => void;
  onChange: (key: keyof ExplorerFilters, value: string) => void;
}) {
  const activeFields = activeKeys.flatMap(key => FILTER_FIELDS.filter(field => field.key === key));
  const availableFields = FILTER_FIELDS.filter(field => !activeKeys.includes(field.key));

  return (
    <Stack gap="xs">
      {activeFields.map(field => {
        const value = filters[field.key] ?? '';
        const invalidUuid = UUID_FIELDS.has(field.key) && !!value && !UUID_PATTERN.test(value);
        return (
          <Group key={field.key} gap="sm" wrap="nowrap" align="flex-start">
            <Text size="sm" w={180} pt={6}>
              {field.label}
            </Text>
            <TextInput
              aria-label={field.label}
              placeholder={field.placeholder}
              type={field.type}
              value={value}
              error={invalidUuid ? 'Not a valid UUID' : undefined}
              onChange={event => onChange(field.key, event.currentTarget.value.trim())}
              style={{ flex: 1, maxWidth: 420 }}
            />
            <ActionIcon
              variant="subtle"
              color="gray"
              mt={4}
              aria-label={`Remove ${field.label.toLowerCase()} filter`}
              onClick={() => onRemove(field.key)}
            >
              <IconX size={16} />
            </ActionIcon>
          </Group>
        );
      })}
      {availableFields.length > 0 && (
        <Menu position="bottom-start" shadow="md">
          <Menu.Target>
            <Button
              variant="subtle"
              size="compact-sm"
              leftSection={<IconPlus size={14} />}
              w="fit-content"
            >
              Add filter
            </Button>
          </Menu.Target>
          <Menu.Dropdown>
            {FILTER_GROUPS.map(group => {
              const groupFields = availableFields.filter(field => field.group === group);
              if (groupFields.length === 0) return null;
              return (
                <Fragment key={group}>
                  <Menu.Label>{group}</Menu.Label>
                  {groupFields.map(field => (
                    <Menu.Item key={field.key} onClick={() => onAdd(field.key)}>
                      {field.label}
                    </Menu.Item>
                  ))}
                </Fragment>
              );
            })}
          </Menu.Dropdown>
        </Menu>
      )}
    </Stack>
  );
}

function ExplorerResults({
  events,
  isLoading,
  isError,
  page,
  totalPages,
  onPageChange,
  highlightClientId,
}: {
  events: ExplorerEvent[] | undefined;
  isLoading: boolean;
  isError: boolean;
  page: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  highlightClientId: string | undefined;
}) {
  if (isLoading) return <PageLoader />;
  if (isError || !events) return <Text c="red">Failed to load meetings</Text>;
  if (events.length === 0) return <Text c="dimmed">No events match these filters.</Text>;

  return (
    <>
      <Table striped highlightOnHover>
        <Table.Thead>
          <Table.Tr>
            <Table.Th />
            <Table.Th>Title</Table.Th>
            <Table.Th>Scheduled</Table.Th>
            <Table.Th>Status</Table.Th>
            <Table.Th>Calendar</Table.Th>
            <Table.Th>Owner</Table.Th>
            <Table.Th>Identifiers</Table.Th>
            <Table.Th>Created</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {events.map(event => (
            <EventRow key={event.id} event={event} highlightClientId={highlightClientId} />
          ))}
        </Table.Tbody>
      </Table>
      <Group justify="center">
        <Pagination total={totalPages} value={page} onChange={onPageChange} />
      </Group>
    </>
  );
}

function toQueryFilters(filters: ExplorerFilters): ExplorerFilters {
  const valid = Object.fromEntries(
    Object.entries(filters).filter(
      ([key, value]) =>
        !!value && (!UUID_FIELDS.has(key as keyof ExplorerFilters) || UUID_PATTERN.test(value)),
    ),
  ) as ExplorerFilters;
  return {
    ...valid,
    scheduledFrom: valid.scheduledFrom && startOfLocalDay(valid.scheduledFrom),
    scheduledTo: valid.scheduledTo && startOfLocalDay(valid.scheduledTo),
  };
}

function startOfLocalDay(date: string): string {
  return dayjs(date).startOf('day').toISOString();
}

function EventRow({
  event,
  highlightClientId,
}: {
  event: ExplorerEvent;
  highlightClientId: string | undefined;
}) {
  const [expanded, setExpanded] = useState(false);
  const owner = fullName(event.ownerFirstName, event.ownerLastName) ?? event.ownerEmailAddress;

  return (
    <Fragment>
      <Table.Tr>
        <Table.Td>
          <ActionIcon
            variant="subtle"
            aria-label={expanded ? 'Hide participants' : 'Show participants'}
            onClick={() => setExpanded(open => !open)}
          >
            {expanded ? <IconChevronDown size={16} /> : <IconChevronRight size={16} />}
          </ActionIcon>
        </Table.Td>
        <Table.Td>
          <Text size="sm" fw={500}>
            {event.title}
          </Text>
          <Monospace>{event.id}</Monospace>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{formatDateTime(event.scheduledStartAt)}</Text>
          <Text size="xs" c="dimmed">
            to {formatDateTime(event.scheduledEndAt)}
          </Text>
        </Table.Td>
        <Table.Td>
          <Group gap={4}>
            {event.isDeleted && (
              <Badge color="red" size="sm">
                Event deleted
              </Badge>
            )}
            {event.calendarIsDeleted && (
              <Badge color="orange" size="sm">
                Calendar deleted
              </Badge>
            )}
            {event.hasRecording && (
              <Badge color="green" variant="light" size="sm">
                Recording
              </Badge>
            )}
            <Badge color="gray" variant="light" size="sm">
              {event.participants.length} participants
            </Badge>
          </Group>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{event.calendarEmail ?? '—'}</Text>
          <Monospace>{event.calendarId}</Monospace>
        </Table.Td>
        <Table.Td>
          <Text size="sm">{owner ?? '—'}</Text>
          <Monospace>{event.ownerUserId ?? '—'}</Monospace>
        </Table.Td>
        <Table.Td>
          <Monospace>Recall: {event.recallEventId ?? '—'}</Monospace>
          <Monospace>iCal: {event.icalUid ?? '—'}</Monospace>
        </Table.Td>
        <Table.Td>
          <Text size="xs">{formatDateTime(event.createdAt)}</Text>
        </Table.Td>
      </Table.Tr>
      {expanded && (
        <Table.Tr>
          <Table.Td />
          <Table.Td colSpan={7}>
            <ParticipantsTable
              participants={event.participants}
              highlightClientId={highlightClientId}
            />
          </Table.Td>
        </Table.Tr>
      )}
    </Fragment>
  );
}

function ParticipantsTable({
  participants,
  highlightClientId,
}: {
  participants: ExplorerParticipant[];
  highlightClientId: string | undefined;
}) {
  if (participants.length === 0) {
    return (
      <Text size="sm" c="dimmed">
        No participant rows.
      </Text>
    );
  }
  return (
    <Table withTableBorder>
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Name</Table.Th>
          <Table.Th>Email</Table.Th>
          <Table.Th>Role</Table.Th>
          <Table.Th>Linked to</Table.Th>
          <Table.Th>Flags</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {participants.map(participant => (
          <Table.Tr
            key={participant.attendanceId ?? participant.participantId ?? participant.emailAddress}
            bg={
              highlightClientId && participant.clientId === highlightClientId
                ? 'var(--mantine-color-yellow-light)'
                : undefined
            }
          >
            <Table.Td>
              <Text size="sm">
                {fullName(participant.firstName, participant.lastName) ?? participant.name ?? '—'}
              </Text>
            </Table.Td>
            <Table.Td>
              <Text size="sm">{participant.emailAddress ?? '—'}</Text>
            </Table.Td>
            <Table.Td>
              <Badge variant="light" size="sm">
                {participant.role}
              </Badge>
            </Table.Td>
            <Table.Td>
              {participant.clientId && <Monospace>Client: {participant.clientId}</Monospace>}
              {participant.userId && <Monospace>User: {participant.userId}</Monospace>}
              {!participant.clientId && !participant.userId && (
                <Text size="xs" c="dimmed">
                  External
                </Text>
              )}
            </Table.Td>
            <Table.Td>
              <Group gap={4}>
                {participant.isDeleted && (
                  <Badge color="red" size="sm">
                    Removed
                  </Badge>
                )}
                {participant.addedInAfternoon && (
                  <Badge color="blue" variant="light" size="sm">
                    Added in Afternoon
                  </Badge>
                )}
              </Group>
            </Table.Td>
          </Table.Tr>
        ))}
      </Table.Tbody>
    </Table>
  );
}

function Monospace({ children }: { children: React.ReactNode }) {
  return (
    <Text size="xs" ff="monospace" c="dimmed" style={{ wordBreak: 'break-all' }}>
      {children}
    </Text>
  );
}

function fullName(firstName: string | null, lastName: string | null): string | null {
  const name = [firstName, lastName].filter(Boolean).join(' ');
  return name || null;
}

function formatDateTime(value: string): string {
  return new Date(value).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' });
}
